import type { InlineInputProps, InlineInputValidateFunction } from '@ztechapps/webui.input'
import { InlineInput, type InlineInputContentOptions } from '@ztechapps/webui.input'
import type { InventoryBySerialNumberValue } from './InventoryBySerialNumberInput'
import { InventoryBySerialNumberInput } from './InventoryBySerialNumberInput'
import assign from 'lodash/assign'
import isEmpty from 'lodash/isEmpty'
import { HorizontalStack } from '@ztechapps/webui.util'
import React from 'react'
import { SaveCancelButtons } from '@ztechapps/webui.button'
import { SerialNumberTooltip } from './SerialNumberTooltip'

type InputProps = {
  documentId: string
}

const defaultUdpateParams =
  (documentId: string) =>
  ({ updatedValue }) => {
    const params = {
      input: {
        quantity: updatedValue.quantity,
        inventoryBySerialNumbers: updatedValue.inventoryBySerialNumbers,
      },
      filter: { 'items._id': updatedValue?._id, _id: documentId },
    }

    return params
  }

const defaultEditContent = (editProps: InlineInputContentOptions) => {
  const { canEdit, saveNow, cancelNow, submitting, setNewValue, value } = editProps

  const onChange = async (e: InventoryBySerialNumberValue) => {
    //we use assign here so it will override the quantity and inventoryBySerialNumbers field
    const mergedValue = assign({}, value, e)

    setNewValue(mergedValue)
  }

  return (
    <HorizontalStack gap={1}>
      <InventoryBySerialNumberInput value={value} onChange={onChange} disabled={!canEdit} />

      <SaveCancelButtons
        saveBusyText=" "
        isBusy={submitting}
        disabled={!canEdit}
        saveText=""
        cancelText=""
        onSave={saveNow}
        onCancel={cancelNow}
      />
    </HorizontalStack>
  )
}
const defaultDisplayContent = (initialValue: any, onClick: any) => {
  const quantity = initialValue?.quantity ?? 0
  const inventoryBySerialNumbers = initialValue.inventoryBySerialNumbers

  return (
    <HorizontalStack gap={1} onClick={onClick}>
      <div>{quantity}</div>

      <SerialNumberTooltip inventoryBySerialNumbers={inventoryBySerialNumbers} />
    </HorizontalStack>
  )
}
const defaultValidate: InlineInputValidateFunction = ({
  quantity,
  inventoryBySerialNumbers,
}: InventoryBySerialNumberValue) => {
  //quantity should not be zero
  if (quantity === 0) {
    throw new Error('Quantity should not be zero.')
  }

  //inventoryBySerialNumbers should not be empty
  if (isEmpty(inventoryBySerialNumbers)) {
    throw new Error('Serial numbers should not be empty.')
  }

  //the count of inventoryBySerialNumbers should be equal to quantity
  if (inventoryBySerialNumbers.length !== Math.abs(quantity)) {
    throw new Error('The count of serial numbers should be equal to quantity.')
  }
}

export function InventoryBySerialNumberInlineInput({
  displayContent,
  editContent,
  updateParams,
  initialValues,
  parentInputComponent,
  inputProps,
  validate,
  ...props
}: Omit<InlineInputProps<InputProps>, 'name'>) {
  const ParentInput = parentInputComponent || InlineInput

  const documentId = inputProps?.documentId

  //we mapped it as item only for the inline input and to map the whole object
  const mappedInitialValues = {
    item: initialValues,
  }

  return (
    <ParentInput
      {...props}
      displayContent={displayContent ?? defaultDisplayContent}
      editContent={editContent ?? defaultEditContent}
      updateParams={updateParams ?? defaultUdpateParams(documentId)}
      validate={validate ?? defaultValidate}
      name="item"
      initialValues={mappedInitialValues}
    />
  )
}

//here are the namespace for this component
InventoryBySerialNumberInlineInput.updateParams = defaultUdpateParams
InventoryBySerialNumberInlineInput.defaultDisplayContent = defaultDisplayContent
InventoryBySerialNumberInlineInput.defaultEditContent = defaultEditContent
